import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import AuthService from 'api/auth.service';
import { AppThunk } from 'reducers/store';
import http from 'api/http';
import authService from 'api/auth.service';
import { find, get, size } from 'lodash';
import { initLocalData } from 'utils';
import {
  AuthState,
  LoginPayload,
  LoginResponse,
  IUserProfileResponse,
  IPostal,
} from './types';
import { clearAppData, updateUserConfig } from '../app/app.reducer';
import warehouseService from 'api/warehouse.service';

const initialState: AuthState = {
  profile: null,
  accessToken: '',
  isLoading: false,
  permissionList: [],
  postalList: [],
  pageList: [],
  extra: [],
  warehouseList: [],
  role: 'default',
  userWarehouse: null,
  currentWarehouse: null,
};

interface ILoginState {
  isLoading: boolean;
}

const authSlices = createSlice({
  name: 'auth',
  initialState: initialState,
  reducers: {
    setLoginState(state: AuthState, { payload }: PayloadAction<ILoginState>) {
      state.isLoading = payload.isLoading;
    },
    loginSuccess(state: AuthState, { payload }: PayloadAction<LoginResponse>) {
      state.accessToken = payload.accessToken;
      state.isLoading = false;
      state.role = payload.role;
      state.warehouseList = payload.warehouseList || [];
      if (payload.role === 'customer') {
        state.loginState = 'DONE';
      }
    },
    upListWarehouse(
      state: AuthState,
      { payload }: PayloadAction<Partial<LoginResponse>>
    ) {
      state.warehouseList = payload.warehouseList;
      if (size(payload.warehouseList) === 1) {
        state.userWarehouse = get(payload, 'warehouseList.0.id');
        state.loginState = 'DONE';
      } else if (state.loginState !== 'DONE') {
        state.loginState = 'STEP2';
      }
      if (state.userWarehouse > 0 && size(state.warehouseList) > 0) {
        state.currentWarehouse = find(
          state.warehouseList,
          (w) => w.id == state.userWarehouse
        );
      }
    },
    doSelectWh(state: AuthState, { payload }: PayloadAction<number>) {
      state.userWarehouse = payload;
      state.loginState = 'DONE';
    },
    updateUserProfile(
      state: AuthState,
      { payload }: PayloadAction<IUserProfileResponse>
    ) {
      state.profile = payload.user;
      state.permissionList = payload.permissionList.map(
        (i) => i.PERMISSION_REF
      );
      state.pageList = payload.pageList;
      state.extra = payload.extra;
    },
    logout(state: AuthState) {
      localStorage.clear();
      state.profile = null;
      state.accessToken = '';
      state.loginState = '';
    },
    changePostal(
      state: AuthState,
      { payload }: PayloadAction<{ accessToken: string }>
    ) {
      state.accessToken = payload.accessToken;
    },
    doSavePostalList(state: AuthState, { payload }: PayloadAction<IPostal[]>) {
      state.postalList = payload;
    },
  },
});

export const {
  logout,
  updateUserProfile,
  loginSuccess,
  setLoginState,
  doSelectWh,
  changePostal,
  doSavePostalList,
  upListWarehouse,
} = authSlices.actions;

export const doLogin =
  (payload: LoginPayload, callback: Function) => async (dispatch) => {
    try {
      dispatch(setLoginState({ isLoading: true }));
      const response = await authService.create(payload);
      const loginResponse = response.data as LoginResponse;
      dispatch(loginSuccess(loginResponse));
      http.configureRequest({ accessToken: loginResponse.accessToken });
      callback && callback();
    } catch (err) {
      dispatch(setLoginState({ isLoading: false }));
    }
  };

export const doLogout = (): AppThunk => async (dispatch, getState) => {
  const token = getState().auth.accessToken;
  if (token) {
    await AuthService.logout(token);
    dispatch(logout());
    dispatch(clearAppData());
    initLocalData();
  }
};

export const refreshProfile = (): AppThunk => async (dispatch) => {
  try {
    const userProfileResponse = await AuthService.getUserProfile();

    const userProfileResponseData =
      userProfileResponse.data as IUserProfileResponse;
    dispatch(updateUserProfile(userProfileResponseData));
    dispatch(updateUserConfig(get(userProfileResponse, 'data.userConfig', {})));
    if (userProfileResponseData.user.role !== 'customer') {
      const uw = await warehouseService.fetchMyWarehouseList({}, null);
      const whList = get(uw.data, 'dataList', []);
      dispatch(upListWarehouse({ warehouseList: whList }));
    }
  } catch (err) {
    if (err.code === 401) {
      doLogout();
      localStorage.clear();
      window.location.reload();
    }
  }
};

export default authSlices.reducer;
